import { io, Socket } from 'socket.io-client';
import Cookies from 'js-cookie';

type MessageHandler = (msg: any) => void;
type StatusChangeHandler = (data: {
  userId: string;
  isOnline: boolean;
}) => void;
type NewWidgetUserHandler = () => void;

const SOCKET_CONFIG = {
  path: '/socket.io',
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 10000,
} as const;

export const createChatService = () => {
  let socket: Socket | null = null;
  let onMessage: MessageHandler | null = null;
  let onStatusChange: StatusChangeHandler | null = null;
  let onNewWidgetUser: NewWidgetUserHandler | null = null;

  const normalizeUrl = (url: string) => {
    if (!/^https?:\/\//.test(url) && !/^wss?:\/\//.test(url)) {
      url = `https://${url}`;
    }
    return url.replace(/^http/, 'ws');
  };

  const attachListeners = () => {
    if (!socket) return;

    socket.on('connect', () =>
      console.info('✅ Socket connected:', socket?.id)
    );
    socket.on('disconnect', (reason) =>
      console.warn('🔌 Disconnected:', reason)
    );
    socket.on('connect_error', (err: any) =>
      console.error('❌ Connection error:', err.message)
    );
    socket.on('newMessage', (data: any) => onMessage?.(data));
    socket.on('userStatusChanged', (data: any) => onStatusChange?.(data));
    socket.on('newWidgetUserCreated', () => onNewWidgetUser?.());
  };

  const connectSocket = async (
    url: string,
    handlers: {
      onMessage?: MessageHandler;
      onStatusChange?: StatusChangeHandler;
      onNewWidgetUser?: NewWidgetUserHandler;
    }
  ) => {
    if (socket && socket.connected) {
      console.info('⚡ Socket already connected');
      return;
    }

    const token = Cookies.get('authToken');
    socket = io(`${normalizeUrl(url)}/company-chat`, {
      ...SOCKET_CONFIG,
      extraHeaders: { Authorization: `Bearer ${token}` },
    });

    onMessage = handlers.onMessage || null;
    onStatusChange = handlers.onStatusChange || null;
    onNewWidgetUser = handlers.onNewWidgetUser || null;

    attachListeners();
  };

  const disconnectSocket = () => {
    if (!socket) return;
    socket.disconnect();
    socket.removeAllListeners();
    socket = null;
  };

  const sendMessage = (text: string, widgetUserId: string): Promise<any> => {
    if (!socket?.connected)
      return Promise.reject(new Error('Socket not connected'));

    const payload = { text: text.trim(), widgetUserId };
    return new Promise((resolve, reject) => {
      socket!.emit('sendMessage', payload, (res: any) => {
        if (res?.error) reject(new Error(res.error));
        else resolve(res);
      });
    });
  };

  return {
    connectSocket,
    disconnectSocket,
    sendMessage,
    isConnected: () => Boolean(socket?.connected),
  };
};

export type ChatService = ReturnType<typeof createChatService>;
